import React, { useEffect, useState } from 'react'
import { Divider } from '@fs/zion-ui'
import { Link, useLocation } from '@fs/zion-router'
import { useTranslation } from 'react-i18next'
import { css } from '@emotion/core'
import { boolAttr } from '@fs/zion-frontend-friends'
import { useFeatureFlag } from '@fs/zion-flags'
import { useAdminContext } from '../../hooks/CenterAdminProvider'

const navBarCss = css`
  overflow-x: auto;
  overflow-y: hidden;
  position: relative;
  white-space: nowrap;
  scroll-behavior: smooth;
  line-height: 2.5rem;
  margin-inline-start: 10px;

  /* Hide menu when printing */
  @media print {
    display: none;
  }
`

const spanCss = css`
  font-family: Verdana, Ayuthaya, sans-serif;
  color: #000000;
  text-decoration: none;
  background: transparent;
  border-radius: 4px;
  border: 0;
  cursor: pointer;
  font-size: 14px;
  margin-inline-end: 5px;
  padding: 4px 10px;
  vertical-align: middle;

  [lang|='zh'] &,
  [lang|='ja'] &,
  [lang|='ko'] & {
    font-size: 16px;
  }

  &:hover {
    background: #e1edc1;
    text-decoration: underline; /* required or else the <a> tag will underline with a color */
  }
  &:focus {
    outline: none;
  }
  &[is-selected] {
    background: #e1edc1;
  }
`

const getDefaultIndex = (pathname) => {
  // this array helps us find out which nav link is selected or highlighted
  const pages = ['/search', '/mycenters', '/admin', '/center-counts', '/custom-center-reports']
  const startIndex = pages.findIndex((page) => pathname.includes(page))
  return startIndex > 0 ? startIndex : 0
}

function NavLink({ path, name, external, index, selectedIndex, setSelectedIndex, dataTestId }) {
  return (
    <Link to={path} external={external} onClick={() => setSelectedIndex(index)} data-testid={dataTestId}>
      <span css={spanCss} index={index} is-selected={boolAttr(selectedIndex === index)}>
        {name}
      </span>
    </Link>
  )
}

export default function NavigationTabs() {
  const centerCountsFlag = useFeatureFlag('center-locations_center-counts')
  const facilityReportsFlag = useFeatureFlag('center-locations_facility-reports')
  const permissions = useAdminContext()
  const { t } = useTranslation()
  // Books and Wiki are hidden for CJK languages at the request of FS CJK specialists
  const { pathname } = useLocation()
  const [selectedIndex, setSelectedIndex] = useState(() => getDefaultIndex(pathname, t))

  useEffect(() => {
    setSelectedIndex(getDefaultIndex(pathname, t))
  }, [pathname, t])

  function navLink({ path, name, index, external = true, dataTestId }) {
    return (
      <NavLink
        path={path}
        name={name}
        index={index}
        selectedIndex={selectedIndex}
        setSelectedIndex={setSelectedIndex}
        external={external}
        dataTestId={dataTestId}
      />
    )
  }
  return permissions.isAdmin || permissions.isSupport ? (
    <>
      <nav css={navBarCss}>
        {(permissions.isAdmin || permissions.isSupport) &&
          navLink({
            path: '/search',
            name: t('centers.tab.internal-search', 'All Locations'),
            index: 0,
            external: false,
            dataTestId: 'internalSearchNav',
          })}
        {permissions.canEdit &&
          navLink({
            path: '/mycenters',
            name: t('centers.tab.centers', 'My Centers'),
            index: 1,
            external: false,
            dataTestId: 'centersNav',
          })}
        {(permissions.isAdmin || permissions.isSupport) &&
          navLink({
            path: '/admin',
            name: t('centers.tab.admin', 'Admin'),
            index: 2,
            external: false,
            dataTestId: 'adminNav',
          })}
        {(permissions.isAdmin || permissions.isSupport) &&
          centerCountsFlag?.isOn &&
          navLink({
            path: '/center-counts',
            name: t('centers.tab.center-counts', 'Center Counts'),
            index: 3,
            external: false,
            dataTestId: 'countNav',
          })}
        {(permissions.isAdmin || permissions.isSupport) &&
          facilityReportsFlag?.isOn &&
          navLink({
            path: '/custom-center-reports',
            name: t('centers.tab.facility-reports', 'Custom Center Reports'),
            index: 4,
            external: false,
            dataTestId: 'customReportsNav',
          })}
      </nav>
      <Divider />
    </>
  ) : null
}
